<template>
    <Views :loading="loading" :error="error">
        <ApplicationFilter :applications="applications" :nodes="nodes" :service-groups="groups" @filter="setFilter" class="mb-4" />

        <div class="d-flex flex-wrap align-center justify-end mb-4" style="gap: 12px">
            <v-select v-model="groupOrder" :items="groupOrders" label="Group order" outlined dense hide-details style="max-width: 240px" />
            <v-select
                v-model="sortBy"
                :items="headers"
                item-value="value"
                item-text="text"
                label="Sort applications by"
                outlined
                dense
                hide-details
                style="max-width: 240px"
            />
            <v-btn
                icon
                :title="sortDesc ? 'Sort ascending' : 'Sort descending'"
                :aria-label="sortDesc ? 'Sort ascending' : 'Sort descending'"
                @click="sortDesc = !sortDesc"
            >
                <v-icon>{{ sortDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
            </v-btn>
        </div>

        <div class="legend mb-3">
            <div v-for="s in statuses" class="item">
                <div class="count" :class="s.color">{{ s.count }}</div>
                <div class="label">{{ s.name }}</div>
            </div>
        </div>

        <div v-if="!items.length" class="empty-state grey--text">No applications found</div>

        <v-card v-for="group in applicationGroups" :key="group.id" outlined class="application-group mb-3">
            <div class="d-flex align-center">
                <v-btn text class="group-heading flex-grow-1" @click="toggleGroup(group)">
                    <v-icon small class="mr-2">{{ isGroupExpanded(group) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                    <v-icon small class="mr-2">mdi-folder</v-icon>
                    <span class="group-name">{{ group.name }}</span>
                    <v-chip small label class="ml-3">{{ group.items.length }}</v-chip>
                    <v-spacer />
                </v-btn>
                <v-btn v-if="group.id !== 'other'" icon small class="mr-3" title="Rename group" aria-label="Rename group" @click="openRename(group)">
                    <v-icon small>mdi-pencil</v-icon>
                </v-btn>
            </div>

            <v-expand-transition>
                <div v-show="isGroupExpanded(group)">
                    <ApplicationTable
                        v-if="group.items.length"
                        :items="group.items"
                        :headers="headers"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    />
                </div>
            </v-expand-transition>
        </v-card>

        <v-dialog v-model="renameDialog" max-width="500" :persistent="savingRename">
            <v-card class="pa-5">
                <v-card-title>Rename group</v-card-title>
                <v-form @submit.prevent="saveRename">
                    <v-text-field v-model="renameValue" label="Group name" outlined dense autofocus maxlength="150" :disabled="savingRename" />
                    <v-alert v-if="renameError" type="error" dense text>{{ renameError }}</v-alert>
                    <v-card-actions>
                        <v-btn text :to="{ name: 'project_settings', params: { tab: 'applications' } }" :disabled="savingRename">Manage groups</v-btn>
                        <v-spacer />
                        <v-btn text :disabled="savingRename" @click="renameDialog = false">Cancel</v-btn>
                        <v-btn color="primary" type="submit" :loading="savingRename" :disabled="!renameValue.trim()">Save</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </Views>
</template>

<script>
import ApplicationFilter from '../components/ApplicationFilter.vue';
import ApplicationTable from '../components/ApplicationTable.vue';
import Views from '@/views/Views.vue';
import { sortApplicationGroups } from '@/utils/application-sorting';

const sortStorageKey = 'application-list-sort';

const statuses = {
    critical: { name: 'SLO violation', color: 'red lighten-1' },
    warning: { name: 'Warning', color: 'orange lighten-1' },
    info: { name: 'Errors in logs', color: 'blue lighten-1' },
    unknown: { name: 'Integration required', color: 'purple lighten-1' },
    ok: { name: 'OK', color: 'green lighten-1' },
};

export default {
    components: { Views, ApplicationFilter, ApplicationTable },

    data() {
        return {
            applications: [],
            filter: new Set(),
            groups: [],
            nodes: [],
            expandedGroups: {},
            sortBy: 'application',
            sortDesc: false,
            groupOrder: 'custom',
            groupOrders: [
                { value: 'custom', text: 'Custom order (Settings)' },
                { value: 'name-asc', text: 'Group name: A–Z' },
                { value: 'name-desc', text: 'Group name: Z–A' },
            ],
            renameDialog: false,
            renameGroupID: '',
            renameValue: '',
            renameError: '',
            savingRename: false,
            loading: false,
            error: '',
        };
    },

    mounted() {
        this.loadSort();
        this.get();
        this.$events.watch(this, this.get, 'refresh');
    },

    watch: {
        sortBy: 'saveSort',
        sortDesc: 'saveSort',
        groupOrder: 'saveSort',
        '$route.params.projectId'() {
            this.applications = [];
            this.groups = [];
            this.nodes = [];
            this.expandedGroups = {};
            this.renameDialog = false;
            this.savingRename = false;
            this.loadSort();
            this.get();
        },
    },

    computed: {
        headers() {
            let headers = [
                { value: 'application', text: 'Application' },
                { value: 'cluster', text: 'Cluster' },
                { value: 'type', text: 'Type' },
                { value: 'errors', text: 'Errors', align: 'end' },
                { value: 'latency', text: 'Latency', align: 'end' },
                { value: 'upstreams', text: 'Upstreams', align: 'end' },
                { value: 'instances', text: 'Instances', align: 'end' },
                { value: 'restarts', text: 'Restarts', align: 'end' },
                { value: 'cpu', text: 'CPU', align: 'end' },
                { value: 'memory', text: 'Mem', align: 'end' },
                { value: 'disk_io_load', text: 'I/O load', align: 'end' },
                { value: 'disk_usage', text: 'Disk', align: 'end' },
                { value: 'network', text: 'Net', align: 'end' },
                { value: 'dns', text: 'DNS', align: 'end' },
                { value: 'logs', text: 'Logs', align: 'center' },
            ];
            if (!this.$api.context.multicluster) {
                return headers.filter((h) => h.value !== 'cluster');
            }
            return headers;
        },
        multicluster() {
            return this.$api.context.multicluster;
        },
        categories() {
            return Array.from(new Set((this.applications || []).map((a) => a.category)).values());
        },
        statuses() {
            return Object.keys(statuses).map((s) => {
                return {
                    ...statuses[s],
                    count: this.items.filter((i) => i.status === s).length,
                };
            });
        },
        items() {
            if (!this.applications) {
                return [];
            }

            const applications = this.applications.filter((a) => this.filter.has(a.id)).map((a) => ({ ...a }));
            const names = {};
            applications.forEach((a) => {
                const id = this.$utils.appId(a.id);
                a.name = id.name;
                a.ns = id.ns;
                if (names[id.name]) {
                    names[id.name]++;
                } else {
                    names[id.name] = 1;
                }
            });
            return applications.map((a) => {
                return {
                    ...a,
                    ns: names[a.name] > 1 ? a.ns : '',
                    color: statuses[a.status].color,
                };
            });
        },
        applicationGroups() {
            const groups = this.groups
                .map((group) => ({
                    ...group,
                    items: this.items.filter((app) => app.group === group.id),
                }))
                .filter((group) => group.items.length);
            const assignedGroups = new Set(this.groups.map((group) => group.id));
            const other = this.items.filter((app) => !assignedGroups.has(app.group));
            if (other.length) {
                groups.push({ id: 'other', name: 'Other applications', expanded: false, items: other });
            }
            return sortApplicationGroups(groups, this.groupOrder);
        },
    },

    methods: {
        get() {
            const projectId = this.$route.params.projectId;
            this.loading = true;
            this.error = '';
            this.$api.getOverview('applications', '', (data, error) => {
                if (projectId !== this.$route.params.projectId) return;
                this.loading = false;
                if (error) {
                    this.error = error;
                    return;
                }
                this.applications = data.applications;
                this.groups = data.application_groups || [];
                this.nodes = data.nodes || [];
            });
        },
        loadSort() {
            const saved = (this.$storage.local(sortStorageKey) || {})[this.$route.params.projectId] || {};
            this.sortBy = this.headers.some((h) => h.value === saved.by) ? saved.by : 'application';
            this.sortDesc = saved.desc === true;
            this.groupOrder = this.groupOrders.some((order) => order.value === saved.groups) ? saved.groups : 'custom';
        },
        saveSort() {
            const saved = this.$storage.local(sortStorageKey) || {};
            saved[this.$route.params.projectId] = { by: this.sortBy, desc: this.sortDesc, groups: this.groupOrder };
            this.$storage.local(sortStorageKey, saved);
        },
        openRename(group) {
            this.renameGroupID = group.id;
            this.renameValue = group.name;
            this.renameError = '';
            this.renameDialog = true;
        },
        saveRename() {
            if (this.savingRename || !this.renameValue.trim()) return;
            const projectId = this.$route.params.projectId;
            const id = this.renameGroupID;
            const name = this.renameValue.trim();
            this.savingRename = true;
            this.renameError = '';
            // Fetch the latest rules so renaming cannot restore stale source patterns.
            this.$api.getApplicationGroups((data, error) => {
                if (projectId !== this.$route.params.projectId) return;
                if (error || !data.groups.some((group) => group.id === id)) {
                    this.savingRename = false;
                    this.renameError = error || 'This group no longer exists. Refresh the list.';
                    return;
                }
                const groups = data.groups.map((group) => (group.id === id ? { ...group, name } : group));
                this.$api.saveApplicationGroups(groups, (data, error) => {
                    if (projectId !== this.$route.params.projectId) return;
                    this.savingRename = false;
                    if (error) {
                        this.renameError = error;
                        return;
                    }
                    this.groups = data.groups || [];
                    this.renameDialog = false;
                });
            });
        },
        setFilter(filter) {
            this.filter = filter;
        },
        isGroupExpanded(group) {
            return Object.prototype.hasOwnProperty.call(this.expandedGroups, group.id) ? this.expandedGroups[group.id] : group.expanded;
        },
        toggleGroup(group) {
            this.$set(this.expandedGroups, group.id, !this.isGroupExpanded(group));
        },
        link(id, report, query) {
            return { name: 'overview', params: { view: 'applications', id, report }, query: { ...query, ...this.$utils.contextQuery() } };
        },
    },
};
</script>

<style scoped>
.table:deep(table) {
    min-width: 500px;
}
.table:deep(tr:hover) {
    background-color: unset !important;
}
.table:deep(th) {
    white-space: nowrap;
}
.table:deep(th),
.table:deep(td) {
    padding: 4px 8px !important;
}
.table:deep(td:has(.application)) {
    padding-left: 0 !important;
}
.table .application {
    display: flex;
    gap: 4px;
}
.table .application .status {
    height: 20px;
    width: 4px;
}
.table .application .name {
    max-width: 30ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .application .cluster {
    max-width: 20ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .logs {
    display: block;
    position: relative;
    height: 100%;
    color: inherit;
}
.table .logs .value {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
}
.table:deep(td:has(.logs)) {
    width: 120px;
    min-width: 120px;
    padding: 0 !important;
}

.type {
    opacity: 60%;
    white-space: nowrap;
}
.type.unknown {
    opacity: 100%;
    border-bottom: 2px solid #ab47bc !important;
    background-color: unset !important;
}
.type.ok {
    opacity: 100%;
}
.type.critical,
.type.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}

.value {
    color: inherit;
    opacity: 60%;
    white-space: nowrap;
}
.value.critical,
.value.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}
.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
}
.legend .item {
    display: flex;
    gap: 4px;
}
.legend .count {
    padding: 0 4px;
    border-radius: 2px;
    height: 18px;
    line-height: 18px;
    color: rgba(255, 255, 255, 0.8);
}
.legend .label {
    opacity: 60%;
}
.application-group {
    overflow: hidden;
}
.group-heading {
    justify-content: flex-start;
    min-height: 48px;
    text-transform: none;
    letter-spacing: normal;
}
.group-name {
    font-size: 16px;
    font-weight: 500;
}
.empty-state {
    padding: 18px 20px;
}
</style>
